import { normalizePlacement } from '@circuit/chat/widget-chrome/tokens';
import {
  BADGE_COLOR,
  LAUNCHER_GAP_PX as IFRAME_LAUNCHER_CLEARANCE_PX,
  IFRAME_MIN_HEIGHT_PX,
  IFRAME_MIN_WIDTH_PX,
  IFRAME_VIEWPORT_MARGIN_PX,
  IFRAME_WIDTH_PX,
  LAUNCHER_ICON_SIZE_PX,
  LAUNCHER_INSET_PX,
  LAUNCHER_SIZE_PX,
} from '@circuit/chat/widget-chrome/tokens';

import { IFRAME_MIN_HEIGHT_PX_FLOATING } from '@/shared/iframeSize';
/**
 * Circuit Chat Embed launcher (IIFE).
 *
 * Bootstrap sequence:
 *   1. Read `apiKey` from the launcher `<script src>`.
 *   2. Fetch `GET /chat-bot/config` with `Authorization: Bearer <apiKey>`.
 *   3. If the fetch succeeds, render the bubble with the agent's branding.
 *      A failed fetch means the widget is unconfigured/broken — the bubble
 *      does NOT render. The bubble click → iframe → SPA handshake is owned
 *      by `createLauncherState` in `./state.ts`.
 *
 * Constraints: no React, no @circuit/* runtime deps, ~3-5 KB gz target.
 */
import { ANY_ORIGIN, PROTOCOL_VERSION } from '@/shared/protocol';

import { launcherLogger } from './beaconLog';
import {
  assertOriginConfigured,
  CHATBOT_CONFIG_PATH,
  CONFIG_FETCH_TIMEOUT_MS,
  EMBED_ORIGIN,
  isAllowedOrigin,
  WIDGET_READY_TIMEOUT_MS,
} from './constants';
import {
  BADGE_ID,
  BUBBLE_ID,
  createBadge,
  createBubble,
  createIndicator,
  IFRAME_HEIGHT_PX,
  IFRAME_ID,
  LAUNCHER_WRAPPER_ID,
  wrapLauncherBubble,
} from './dom';
import { fetchChatbotConfig } from './fetchChatbotConfig';
import {
  type MountArgs,
  createLauncherState,
  __testing as stateTestingBarrel,
} from './state';

interface LauncherDependencies {
  /** Test seam — override the fetch used to load the chatbot-config. */
  fetch?: typeof globalThis.fetch;
  /** The customer-facing `<script>` tag that loaded this IIFE. */
  scriptEl: HTMLScriptElement | null;
}

/** Public API attached to `window.CircuitChat`. */
export interface CircuitChatGlobalApi {
  close: () => void;
  open: () => void;
}

export function extractApiKey(scriptSrc: string): string {
  try {
    const url = new URL(scriptSrc, globalThis.location.href);
    return url.searchParams.get('apiKey') ?? '';
  } catch {
    return '';
  }
}

// Strict `=== 'true'` to match the v1 chat-widget's `open` param semantics.
export function extractDefaultOpen(scriptSrc: string): boolean {
  try {
    const url = new URL(scriptSrc, globalThis.location.href);
    return url.searchParams.get('open') === 'true';
  } catch {
    return false;
  }
}

/**
 * Wire DOM elements + global API around a fresh `LauncherState`.
 */
function buildGlobalApi(
  state: ReturnType<typeof createLauncherState>,
): CircuitChatGlobalApi {
  return {
    close: () => {
      state.hideIframe();
      state.postToSpa('WIDGET_CLOSE');
    },
    open: () => {
      state.openWidget();
      state.postToSpa('WIDGET_OPEN');
    },
  };
}

function mountLauncher(args: MountArgs): () => void {
  const state = createLauncherState(args);
  const openByDefault = args.config.branding.openByDefault ?? false;

  if (openByDefault) {
    // No bubble DOM node — `window.CircuitChat` is still wired for host-page control.
    (globalThis as { CircuitChat?: CircuitChatGlobalApi }).CircuitChat =
      buildGlobalApi(state);
    state.openWidget();
    state.postToSpa('WIDGET_OPEN');
    return function dispose(): void {
      state.teardown();
      delete (globalThis as { CircuitChat?: CircuitChatGlobalApi }).CircuitChat;
    };
  }

  const cornerPosition = normalizePlacement(args.config.branding.position);

  const bubble = createBubble(args.config.branding, args.config.agent.name, {
    onIntent: state.prewarmIframe,
    onClick: () => {
      // Toggle open/close using explicit widget-open state so a click during
      // the reveal/close fade doesn't misfire (opacity is unreliable mid-transition).
      if (state.isWidgetOpen()) {
        state.hideIframe();
        state.postToSpa('WIDGET_CLOSE');
      } else {
        state.openWidget();
        state.postToSpa('WIDGET_OPEN');
      }
    },
  });
  const badge = createBadge(cornerPosition);
  const indicator = createIndicator(cornerPosition, false);
  const wrapper = wrapLauncherBubble(bubble, indicator, cornerPosition);
  state.setBubble(bubble);
  state.setBadge(badge);
  state.setIndicator(indicator);
  document.body.appendChild(wrapper);
  document.body.appendChild(badge);

  // Programmatic API for host pages: `window.CircuitChat.open()` etc.
  // WIDGET_OPEN / WIDGET_CLOSE notifications let the SPA react (focus the
  // prompt input on open, etc.).
  const globalApi = buildGlobalApi(state);
  (globalThis as { CircuitChat?: CircuitChatGlobalApi }).CircuitChat =
    globalApi;

  // `?open=true`: start opened instead of collapsed. The WIDGET_OPEN post is
  // queued by the launcher state until the SPA signals WIDGET_READY.
  if (args.defaultOpen === true) {
    state.openWidget();
    state.postToSpa('WIDGET_OPEN');
  }

  return function dispose(): void {
    state.teardown();
    delete (globalThis as { CircuitChat?: CircuitChatGlobalApi }).CircuitChat;
  };
}

/**
 * Entry point. Resolves to a `dispose()` once the config fetch settles and
 * the bubble is mounted. Production drops the promise; tests await it for
 * deterministic teardown.
 *
 * Idempotent under concurrent invocation: two init() calls racing on the
 * same page both check the bubble-present guard — the loser returns a
 * no-op dispose rather than double-mounting. The check happens both
 * before the async fetch AND after it, since two concurrent init()s can
 * each pass the initial guard, both fetch, and both try to mount.
 */
export async function init(
  deps: LauncherDependencies = {
    scriptEl: document.currentScript as HTMLScriptElement | null,
  },
): Promise<() => void> {
  assertOriginConfigured();

  // Pre-fetch guard — catches the common synchronous case (script tag
  // injected twice on a warm page). Check both the bubble AND the iframe:
  // when the config drives `openByDefault`, no bubble is ever created so
  // the bubble ID alone doesn't detect a prior mount.
  if (
    document.getElementById(BUBBLE_ID) ||
    document.getElementById(IFRAME_ID)
  ) {
    return () => undefined;
  }

  const scriptSrc = deps.scriptEl?.src ?? '';
  const apiKey = extractApiKey(scriptSrc);
  const fetcher = deps.fetch ?? globalThis.fetch.bind(globalThis);

  // Announce launcher init as an info-level breadcrumb. This is the "the
  // customer's page loaded our IIFE at all" signal — aggregating these in
  // Datadog gives us a top-of-funnel count to compare against widget-open
  // and WIDGET_READY rates.
  launcherLogger.info({
    event: { type: 'chat_embed.launcher_init' },
    message: 'launcher: init',
  });

  // Fetch BEFORE rendering so branding lands without a flash. A failed
  // fetch means the widget is unconfigured/broken — don't render the
  // bubble at all. fetchChatbotConfig already console.warned the cause.
  const config = await fetchChatbotConfig(apiKey, fetcher);
  if (!config) return () => undefined;

  // Post-fetch guard — two concurrent init() calls can both pass the
  // pre-fetch guard, both complete their fetches, and both reach here.
  // The loser returns a no-op dispose rather than mounting a second widget.
  if (
    document.getElementById(BUBBLE_ID) ||
    document.getElementById(IFRAME_ID)
  ) {
    return () => undefined;
  }

  return mountLauncher({
    apiKey,
    config,
    defaultOpen: extractDefaultOpen(scriptSrc),
    scriptSrc,
  });
}

/** @internal Test-only barrel. */
// eslint-disable-next-line no-underscore-dangle, @typescript-eslint/naming-convention
export const __testing = {
  ...stateTestingBarrel,
  ANY_ORIGIN,
  BADGE_COLOR,
  BADGE_ID,
  BUBBLE_ID,
  CHATBOT_CONFIG_PATH,
  CONFIG_FETCH_TIMEOUT_MS,
  EMBED_ORIGIN,
  extractApiKey,
  extractDefaultOpen,
  fetchChatbotConfig,
  IFRAME_HEIGHT_PX,
  IFRAME_ID,
  IFRAME_LAUNCHER_CLEARANCE_PX,
  IFRAME_MIN_HEIGHT_PX,
  IFRAME_MIN_HEIGHT_PX_FLOATING,
  IFRAME_MIN_WIDTH_PX,
  IFRAME_VIEWPORT_MARGIN_PX,
  IFRAME_WIDTH_PX,
  isAllowedOrigin,
  LAUNCHER_ICON_SIZE_PX,
  LAUNCHER_INSET_PX,
  LAUNCHER_SIZE_PX,
  LAUNCHER_WRAPPER_ID,
  PROTOCOL_VERSION,
  WIDGET_READY_TIMEOUT_MS,
};
